import {
  apiPostMachineReport,
  apiPutMachineReport,
} from "@app/api/machines/machines.api";
import { MachineReportMediaPayload, MachineReportRecordingPayload, uploadMediaFiles, uploadRecording, uploadSignature } from "../Inspection/createInspectionSubmit.helper";
import type { ExistingRecordingMeta } from "../Refuel/createRefuelSubmit.helper";


export type CreateAbnormalityFormDraft = {
  title: string;
  comment: string;
  mediaFiles: File[];
  recordedBlob: Blob | null;
  serviceMeterValue: string;
  odometerValue: string;
};

export type CreateAbnormalityApiMedia = {
  fileName: string;
  mediaUrl: string;
  mimeType: string;
  captureMediaAt: string;
};

export type CreateAbnormalityApiRecording = {
  fileName: string;
  recordingUrl: string;
  mimeType: string;
  recordingAt: string;
};

export type CreateAbnormalityApiPayload = {
  reportTitle: string;
  reportComment: string;
  signatureUrl?: string;
  serviceMeterInHour?: number;
  odometer?: number;
  machineReportMedias: CreateAbnormalityApiMedia[];
  machineReportRecordings: CreateAbnormalityApiRecording[];
};

export type UpdateAbnormalityApiPayload = {
  reportTitle: string;
  reportComment: string;
  lastUpdatedSignatureUrl?: string;
  serviceMeterInHour?: number;
  odometer?: number;
  machineReportMedias: CreateAbnormalityApiMedia[];
  machineReportRecordings: CreateAbnormalityApiRecording[];
};

type ExistingMediaFile = File & {
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  captureMediaAt?: string;
  filePath?: string;
};

function extractFilePathFromUrl(url?: string | null): string {
  if (!url) return "";
  const match = url.match(/contents\/([^?]+)/i);
  if (match?.[1]) {
    return decodeURIComponent(match[1]);
  }
  return url;
}

function extractFileNameFromUrl(url?: string | null): string {
  if (!url) return "";
  const path = extractFilePathFromUrl(url);
  const parts = path.split("/");
  return parts[parts.length - 1] || "";
}

function normalizeExistingMediaUrl(
  mediaUrl?: string | null,
  filePath?: string | null,
): string {
  return filePath || extractFilePathFromUrl(mediaUrl);
}

function normalizeExistingRecordingUrl(
  recordingUrl?: string | null,
  filePath?: string | null,
): string {
  return filePath || extractFilePathFromUrl(recordingUrl);
}

async function buildUpdateMediaFiles(
  mediaFiles: File[],
): Promise<CreateAbnormalityApiMedia[]> {
  const existing: CreateAbnormalityApiMedia[] = [];
  const newFiles: File[] = [];

  for (const file of mediaFiles) {
    const media = file as ExistingMediaFile;
    if (file instanceof File && !media.mediaUrl) {
      newFiles.push(file);
      continue;
    }

    if (media.mediaUrl) {
      existing.push({
        fileName:
          media.fileName ||
          media.name ||
          extractFileNameFromUrl(media.mediaUrl),
        mediaUrl: normalizeExistingMediaUrl(media.mediaUrl, media.filePath),
        mimeType: media.mimeType || media.type || "application/octet-stream",
        captureMediaAt: media.captureMediaAt || new Date().toISOString(),
      });
    }
  }

  const uploaded = (await uploadMediaFiles(newFiles)).map(toApiMedia);
  return [...existing, ...uploaded];
}

async function buildUpdateRecordings(
  recordedBlob: Blob | null,
  existingRecording?: ExistingRecordingMeta | null,
  keepExistingRecording = false,
): Promise<CreateAbnormalityApiRecording[]> {
  if (recordedBlob && !keepExistingRecording) {
    return (await uploadRecording(recordedBlob)).map(toApiRecording);
  }

  if (!existingRecording?.recordingUrl && !existingRecording?.filePath) {
    return [];
  }

  const recordingUrl = normalizeExistingRecordingUrl(
    existingRecording.recordingUrl,
    existingRecording.filePath,
  );

  return [
    {
      fileName:
        existingRecording.fileName ||
        extractFileNameFromUrl(recordingUrl) ||
        "recording.m4a",
      recordingUrl,
      mimeType: existingRecording.mimeType || "audio/m4a",
      recordingAt: existingRecording.recordingAt || new Date().toISOString(),
    },
  ];
}

function buildAbnormalityPayloadFields(
  draft: CreateAbnormalityFormDraft,
): Pick<
  CreateAbnormalityApiPayload,
  "reportTitle" | "reportComment" | "serviceMeterInHour" | "odometer"
> {
  const payload: Pick<
    CreateAbnormalityApiPayload,
    "reportTitle" | "reportComment" | "serviceMeterInHour" | "odometer"
  > = {
    reportTitle: draft.title.trim(),
    reportComment: draft.comment.trim(),
  };

  const serviceMeterInHour = parseOptionalNumeric(draft.serviceMeterValue);
  const odometer = parseOptionalNumeric(draft.odometerValue);

  if (serviceMeterInHour !== undefined) {
    payload.serviceMeterInHour = serviceMeterInHour;
  }
  if (odometer !== undefined) {
    payload.odometer = odometer;
  }

  return payload;
}

function parseOptionalNumeric(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toApiMedia(media: MachineReportMediaPayload): CreateAbnormalityApiMedia {
  return {
    fileName: media.fileName,
    mediaUrl: media.mediaUrl,
    mimeType: media.mimeType,
    captureMediaAt: media.captureMediaAt,
  };
}

function toApiRecording(
  recording: MachineReportRecordingPayload,
): CreateAbnormalityApiRecording {
  return {
    fileName: recording.fileName,
    recordingUrl: recording.recordingUrl,
    mimeType: recording.mimeType,
    recordingAt: recording.recordingAt,
  };
}

export async function submitCreateAbnormality(params: {
  groupId: string;
  machineId: string;
  draft: CreateAbnormalityFormDraft;
  signatureDataUrl: string | null;
}): Promise<{ successMessage?: string }> {
  const { groupId, machineId, draft, signatureDataUrl } = params;

  const signatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const machineReportMedias = (
    await uploadMediaFiles(draft.mediaFiles)
  ).map(toApiMedia);

  const machineReportRecordings = (
    await uploadRecording(draft.recordedBlob)
  ).map(toApiRecording);

  const payload: CreateAbnormalityApiPayload = {
    ...buildAbnormalityPayloadFields(draft),
    ...(signatureUrl ? { signatureUrl } : {}),
    machineReportMedias,
    machineReportRecordings,
  };

  const response = await apiPostMachineReport(groupId, machineId, payload);
  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}

export async function submitUpdateAbnormality(params: {
  groupId: string;
  machineId: string;
  machineReportId: string;
  draft: CreateAbnormalityFormDraft;
  signatureDataUrl: string | null;
  existingRecording?: ExistingRecordingMeta | null;
  keepExistingRecording?: boolean;
}): Promise<{ successMessage?: string }> {
  const {
    groupId,
    machineId,
    machineReportId,
    draft,
    signatureDataUrl,
    existingRecording,
    keepExistingRecording = false,
  } = params;

  const lastUpdatedSignatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const machineReportMedias = await buildUpdateMediaFiles(draft.mediaFiles);
  const machineReportRecordings = await buildUpdateRecordings(
    draft.recordedBlob,
    existingRecording,
    keepExistingRecording,
  );

  const payload: UpdateAbnormalityApiPayload = {
    ...buildAbnormalityPayloadFields(draft),
    ...(lastUpdatedSignatureUrl ? { lastUpdatedSignatureUrl } : {}),
    machineReportMedias,
    machineReportRecordings,
  };

  const response = await apiPutMachineReport(
    groupId,
    machineId,
    machineReportId,
    payload,
  );

  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}
